import { cookieStorage } from "./cookieStorage";
import { logger } from "./logger";

const AUTH_STORAGE_KEY = "auth-storage";
const MIGRATION_FLAG_KEY = "auth-storage-migrated";

/**
 * Check if migration has already been performed
 */
function hasMigrated(): boolean {
  return localStorage.getItem(MIGRATION_FLAG_KEY) === "true";
}

/**
 * Mark migration as complete
 */
function markMigrated(): void {
  localStorage.setItem(MIGRATION_FLAG_KEY, "true");
}

/**
 * Migrate auth storage from localStorage to cookies
 *
 * This function:
 * 1. Checks if migration is needed (data exists in localStorage but not in cookies)
 * 2. Copies the data from localStorage to cookies
 * 3. Removes the localStorage entry to avoid confusion
 * 4. Sets a flag to prevent re-running migration
 *
 * Safe to call multiple times - will only migrate once.
 */
export function migrateAuthStorage(): void {
  // Skip if already migrated
  if (hasMigrated()) {
    return;
  }

  try {
    // Check if there's existing data in localStorage
    const localStorageData = localStorage.getItem(AUTH_STORAGE_KEY);

    if (!localStorageData) {
      // No data to migrate
      markMigrated();
      return;
    }

    // Check if cookie already has data (user might have logged in after update)
    const cookieData = cookieStorage.getItem(AUTH_STORAGE_KEY);

    if (cookieData) {
      // Cookie already has data, prefer it and clean up localStorage
      localStorage.removeItem(AUTH_STORAGE_KEY);
      markMigrated();
      return;
    }

    // Migrate data from localStorage to cookies
    cookieStorage.setItem(AUTH_STORAGE_KEY, localStorageData);

    // Clean up localStorage
    localStorage.removeItem(AUTH_STORAGE_KEY);

    // Mark migration as complete
    markMigrated();

    // eslint-disable-next-line no-console
    logger.info("[Auth] Successfully migrated auth storage from localStorage to cookies");
  } catch (error) {
    // Migration failed, but don't block app startup
    // User will need to log in again
    // eslint-disable-next-line no-console
    logger.error(`[Auth] Failed to migrate auth storage: ${error}`);
  }
}
